import {useMemo, useState} from 'react';
import {indexOf, reverse, sortBy} from 'lodash';
import {getContractCircleStatus} from '../../helpers';
import {DashboardContractData} from '../../../../../../shared/types/api-v2.types';
import {SORT_FIELDS, SORT_ORDERS, STATUS_COLOR_PRIORITY} from './constants';

const LIST_STATUS_PRIORITIES = {
  asc: STATUS_COLOR_PRIORITY,
  desc: reverse([...STATUS_COLOR_PRIORITY]),
};

type SortField = keyof typeof SORT_FIELDS;
type SortOrder = keyof typeof SORT_ORDERS;

export function useSortContracts(contracts: DashboardContractData[]) {
  const [sortField, setSortField] = useState<SortField>(SORT_FIELDS.status);
  const [sortOrder, setSortOrder] = useState<SortOrder>(SORT_ORDERS.none);

  const handleSort = (field: SortField) => {
    if (sortField !== field) {
      setSortField(field);
      setSortOrder(SORT_ORDERS.asc);
    } else {
      setSortOrder((prev) =>
        prev === SORT_ORDERS.none ? SORT_ORDERS.asc : prev === SORT_ORDERS.asc ? SORT_ORDERS.desc : SORT_ORDERS.none,
      );
    }
  };

  const sortedContracts = useMemo(() => {
    if (!contracts) return [];
    if (sortOrder === SORT_ORDERS.none) return contracts;

    if (sortField === SORT_FIELDS.status) {
      const priority = LIST_STATUS_PRIORITIES[sortOrder];
      return sortBy(contracts, (contract) => {
        const status = getContractCircleStatus(contract, {
          getStatus: (r) => r.lastStatus,
          getPlannedDate: (r) => r.plannedDate,
        });
        const index = indexOf(priority, status.color);
        return index === -1 ? priority.length : index;
      });
    }

    if (sortField === SORT_FIELDS.contractNumber) {
      const sorted = sortBy(contracts, (c) => (c.contract?.contractNumber || '').toLowerCase());
      return sortOrder === SORT_ORDERS.asc ? sorted : sorted.reverse();
    }

    if (sortField === SORT_FIELDS.companyName) {
      const sorted = sortBy(contracts, (c) => (c.contract?.companyName || '').toLowerCase());
      return sortOrder === SORT_ORDERS.asc ? sorted : sorted.reverse();
    }

    return contracts;
  }, [contracts, sortField, sortOrder]);

  return {
    sortField,
    sortOrder,
    handleSort,
    sortedContracts,
  };
}
